import { reportIdentityMismatch } from '@airclou/auth-react'

const TOKEN_KEY = 'dashboard:authToken'

/**
 * Read a JSON body without consuming the caller's response.
 *
 * `clone` is guarded because test doubles for fetch in this repo are plain
 * `{ ok, status, json }` objects (see src/api/workspaces.test.ts); without the
 * guard this helper turns a mocked 401 into a TypeError.
 */
async function peekBody(response: Response): Promise<any> {
  if (typeof (response as Response).clone !== 'function') return null
  return response.clone().json().catch(() => null)
}

/**
 * Swap in the token the provider just derived.
 *
 * The caller built its headers from a token that has since been replaced, so a
 * retry that reuses them is refused all over again. Everything else about the
 * request is left alone.
 */
function withCurrentToken(init: RequestInit | undefined): RequestInit | undefined {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) return init
  const headers = new Headers(init?.headers as HeadersInit | undefined)
  headers.set('Authorization', `Bearer ${token}`)
  return { ...(init || {}), headers }
}

/**
 * Guarantee a Bearer header on the first attempt.
 *
 * A caller that forgets its own `Authorization` header sends a cookie-only
 * request — which the server cannot refuse as a two-identity request (there is
 * only one credential), so it silently answers as whoever holds the shared
 * `.airclou.com` cookie: the exact wrong-user bug this helper exists to stop
 * (ID-3). A header the caller *did* set is left alone — pages deliberately send
 * the identity they are displaying, which may be a fresher React-state token.
 */
function withDefaultToken(init: RequestInit | undefined): RequestInit | undefined {
  const headers = new Headers(init?.headers as HeadersInit | undefined)
  if (headers.has('Authorization')) return init
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) return init
  headers.set('Authorization', `Bearer ${token}`)
  return { ...(init || {}), headers }
}

/**
 * `fetch` for calls that go to the **Auth service** rather than this app's own
 * backend.
 *
 * Those calls bypass `DashboardApiClient.request()`, so they never saw its 401
 * handling. That matters for exactly one code: the auth service answers `401
 * IDENTITY_MISMATCH` when the session cookie and the Bearer header name
 * different users, and the correct response is to have the auth provider
 * re-derive the stored token from the cookie — not to treat it as a normal 401
 * and sign the user out.
 *
 * Then **retry reads once**. Re-deriving the token fixes the session but leaves
 * this particular request failed, and in the captured case (two accounts in one
 * tenant) `tenantId` never changes, so the page's own effects do not re-fire
 * either — the operator would sit on an error whose only escape is a reload.
 * Writes are never replayed: the re-derived token names a different user, and
 * replaying the caller's payload under it is a cross-account write.
 *
 * Use this instead of bare `fetch` for every request to the Auth service.
 * See apps/auth/docs/session-credential-standardization-plan.md (ID-4).
 */
export async function authServiceFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, withDefaultToken(init))
  if (response.status !== 401) return response

  const wasMismatch = await reportIdentityMismatch(response.status, await peekBody(response))
  if (!wasMismatch) return response

  // Only reads are replayed. The re-derived token names a *different* user than
  // the one the caller built this request for, so replaying a write would land
  // user A's form data on user B's account (save A's recovery email onto B,
  // revoke B's sessions). The reconcile above has already healed the session;
  // the write itself must fail visibly and be redone by a human who can see
  // whose account the page now shows.
  const method = (init?.method ?? 'GET').toUpperCase()
  if (method !== 'GET' && method !== 'HEAD') return response

  // The caller's deadline covers this whole helper, and the reconcile above just
  // spent part of it — up to its own 15s. If that budget is gone, retrying can
  // only throw `AbortError`, turning a 401 the caller knows how to handle into a
  // network error it does not. Hand back the original response instead.
  if ((init?.signal as AbortSignal | undefined)?.aborted) return response

  // The provider has finished re-deriving; this attempt uses the new token.
  // Deliberately not a loop — a second refusal means the mismatch is not the
  // stale-token kind, and the caller's own 401 handling should see it.
  try {
    return await fetch(input, withCurrentToken(init))
  } catch {
    // Aborted mid-retry, or the network went away. The first response is still
    // unread (we only ever cloned it), so it is the honest thing to return.
    return response
  }
}
